// @ts-nocheck
import React, { CSSProperties } from 'react';
import cx from 'classnames';
import Modal from './modal';
import Button from './button';
import KeyboardEvents from './keyboard-events';
import css from './css';
import Theme from './theme';

interface Props {
  show: boolean;
  title?: string;
  width?: number;
  tooltip?: string;
  showTop?: boolean;
  negative?: string;
  neutral?: string;
  positive?: string;
  tooltipDelay?: number;
  children: React.ReactNode;
  maxHeight?: number | string;
  minHeight?: number | string;
  customStyles?: React.CSSProperties;
  bodyClassName?: string;
  processingData?: boolean;
  keyboardDisabled?: boolean;
  negativeDisabled?: boolean;
  neutralDisabled?: boolean;
  negativeHidden?: boolean;
  positiveHidden?: boolean;
  positiveDisabled?: boolean;
  positiveButtonClassName?: string;
  negativeButtonClassName?: string;
  loadingLabel?: string;
  zIndex?: number;
  onNegativeClick?(event: any): void;
  onNeutralClick?(event: any): void;
  onPositiveClick?(event: any): void;
}

interface State {
  visible: boolean;
}

const modalTopStyles: CSSProperties = {
  top: 0,
  margin: '25px auto',
  marginTop: '25px !important',
  maxWidth: 800,
  left: 0,
  right: 0,
  overflow: 'auto',
  maxHeight: '95%',
  display: '-webkit-box',
};

export default class Dialog extends React.Component<Props, State> {
  state: State = {
    visible: this.props.show,
  };

  static defaultProps = {
    show: true,
    negative: 'Cancel',
    positive: 'Okay',
    width: 500,
  };

  modal: HTMLDivElement;

  componentDidUpdate(prevProps: Props) {
    if (this.props.show !== prevProps.show) {
      window.requestAnimationFrame(() => {
        if (this.modal && !this.props.showTop) {
          this.modal.style.marginTop = `${(-this.modal.offsetHeight / 2).toString()}px`;
        }
      });
    }
  }

  render() {
    const alignModalTop = this.props.showTop && { ...modalTopStyles };

    const style = {
      width: this.props.width,
      marginLeft: !this.props.showTop && -(this.props.width / 2),
      ...alignModalTop,
    };

    const { customStyles, zIndex } = this.props;

    return (
      <Modal
        show={this.props.show}
        dialogTransitionTimeout={150}
        backdropTransitionTimeout={150}
        customModalStyles={zIndex ? { zIndex } : undefined}
      >
        <div
          className={cx('modal-content', styles.alert)}
          ref={this.bindNode}
          style={{ ...style, ...customStyles }}
        >
          <KeyboardEvents onKeyUp={this.handleKeyUp}>
            {this.renderTitle()}
            {this.renderBody()}
            {this.renderButtons()}
          </KeyboardEvents>
        </div>
      </Modal>
    );
  }

  renderTitle() {
    if (this.props.title) {
      return (
        <h4 className={styles.title}>{this.props.title}</h4>
      );
    }

    return null;
  }

  renderBody() {
    return (
      <div
        style={{ maxHeight: this.props.maxHeight, minHeight: this.props.minHeight }}
        className={cx(this.props.bodyClassName, styles.body)}
      >
        {this.props.children}
      </div>
    );
  }

  renderButtons() {
    if (this.props.negative || this.props.positive || this.props.neutral) {
      return (
        <div className={styles.buttons}>
          {this.renderNegativeButton()}
          {this.renderNeutralButton()}
          {this.renderPositiveButton()}
        </div>
      );
    }

    return null;
  }

  renderNeutralButton() {
    if (this.props.neutral && !this.props.processingData) {
      return (
        <Button
          text
          key="neutral"
          disabled={!!this.props.neutralDisabled}
          className={cx(styles.button, styles.neutral)}
          onClick={this.handleNeutralClick}
        >
          {this.props.neutral}
        </Button>
      );
    }

    return null;
  }

  renderNegativeButton() {
    if (this.props.negative && !this.props.processingData && !this.props.negativeHidden) {
      return (
        <Button
          text
          textCancel
          key="negative"
          disabled={!!this.props.negativeDisabled}
          className={this.props.negativeButtonClassName}
          onClick={this.handleNegativeClick}
        >
          {this.props.negative}
        </Button>
      );
    }

    return null;
  }

  renderPositiveButton() {
    if (this.props.positive && !this.props.positiveHidden) {
      return (
        <Button
          text
          key="positive"
          disabled={this.props.positiveDisabled || this.props.processingData}
          tooltip={this.props.positiveDisabled && this.props.tooltip}
          tooltipDelay={this.props.tooltipDelay}
          className={this.props.positiveButtonClassName}
          onClick={this.handlePositiveClick}
        >
          {!this.props.processingData ? this.props.positive : this.props.loadingLabel}
        </Button>
      );
    }

    return null;
  }

  handleNegativeClick = (event) => {
    event.stopPropagation();
    this.props.onNegativeClick(event);
  };

  handleNeutralClick = (event) => {
    event.stopPropagation();
    this.props.onNeutralClick(event);
  };

  handlePositiveClick = (event) => {
    event.stopPropagation();
    this.props.onPositiveClick(event);
  };

  handleKeyUp = (event) => {
    if (!this.props.keyboardDisabled) {
      if (event.target && event.target.tagName.toLowerCase() === 'textarea') {
        return;
      }

      if (event.keyCode === 13) {
        if (this.props.onPositiveClick) {
          this.props.onPositiveClick(event);
        }
      } else if (event.keyCode === 27) {
        if (this.props.onNegativeClick) {
          this.props.onNegativeClick(event);
        }
      }
    }
  };

  bindNode = (node) => {
    this.modal = node;
  };
}

const styles = css({
  alert: {
    position: 'fixed',
    backgroundColor: '#fff',
    left: '50%',
    top: '30%',
    borderRadius: 3,
    fontFamily: Theme.fontFamily,
    padding: 30,
    boxShadow: '0 14px 45px rgba(0,0,0,0.25), 0 10px 18px rgba(0,0,0,0.22)',
  },

  title: {
    marginTop: 0,
    marginBottom: 10,
  },

  buttons: {
    textAlign: 'right',
    marginTop: 10,
  },

  button: {
  },

  label: {
    fontSize: '13px',
    fontWeight: 'normal',
    lineHeight: '18px',
  },

  body: {
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
  },
});
